import { Search } from '@mui/icons-material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useSubscription } from '@/hooks/useSubscription';
import { EntitySummary } from '@/lib/types';

interface BoardFiltersProps {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  filterPov: string;
  setFilterPov: (v: string) => void;
  filterLocation: string;
  setFilterLocation: (v: string) => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  characters: Record<string, EntitySummary>;
  locations: Record<string, EntitySummary>;
}

export function BoardFilters({
  searchTerm,
  setSearchTerm,
  filterPov,
  setFilterPov,
  filterLocation,
  setFilterLocation,
  filterStatus,
  setFilterStatus,
  characters,
  locations,
}: BoardFiltersProps) {
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems="flex-end"
      sx={{ mb: 1.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <TextField
        placeholder={t('entity_list.scenes.search_placeholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: <Search sx={{ fontSize: 18, mr: 1, opacity: 0.5 }} />,
          sx: {
            borderRadius: 2,
            bgcolor: 'background.paper',
          }
        }}
        sx={{ flexGrow: 1 }}
      />

      <Stack direction="row" spacing={2} alignItems="flex-end">
        <FilterSelect
          label={t('entity_form.scenes.pov_label')}
          value={filterPov}
          onChange={setFilterPov}
          options={Object.values(characters)}
          allLabel={t('entity_list.scenes.all_povs')}
        />
        <FilterSelect
          label={t('entity_form.scenes.location_label')}
          value={filterLocation}
          onChange={setFilterLocation}
          options={Object.values(locations)}
          allLabel={t('entity_list.scenes.all_locations')}
        />
        {isGenerativeAiAllowed && (
          <FilterSelect
            label={t('entity_form.scenes.status_label')}
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { id: 'idle', name: t('writers_room.phases.idle') },
              { id: 'prepared', name: t('writers_room.phases.prepared') },
              { id: 'writing', name: t('writers_room.phases.writing') },
              { id: 'editing', name: t('writers_room.phases.editing') },
              { id: 'validating', name: t('writers_room.phases.validating') },
              { id: 'approved', name: t('writers_room.phases.approved') },
              { id: 'ready', name: t('writers_room.phases.ready') }
            ]}
            allLabel={t('entity_list.scenes.all_status')}
          />
        )}
      </Stack>
    </Stack>
  );
}

interface FilterSelectOption {
  id: string;
  name: string;
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: FilterSelectOption[];
  allLabel: string;
}

function FilterSelect({ label, value, onChange, options, allLabel }: FilterSelectProps) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5, letterSpacing: '0.05em', fontWeight: 800 }}>
        {label}
      </Typography>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        size="small"
        sx={{
          minWidth: 160,
          bgcolor: 'background.paper',
          borderRadius: 2,
          height: 36,
          '& .MuiSelect-select': { py: 1, fontWeight: 600, fontSize: '0.8rem' },
        }}
      >
        <MenuItem value="all">{allLabel}</MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

